import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { Amount, ServiceCatalogSelectionsForAllTravelers, TravelerService } from '@fcom/dapi/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { BookingAppState } from '@fcom/common/interfaces/booking';

import { EligibilitiesForFragments, FlightWithStreams, SeatChangeEvent } from '../../../interfaces';
import { seatSelections } from '../../../../../store';
import { servicesForFlightAndTravelerId } from '../../../utils';
import { AncillaryLoaderStatus } from '../../../enums';

@Component({
  selector: 'fin-ancillary-seat-map',
  templateUrl: './ancillary-seat-map.component.html',
})
export class AncillarySeatMapComponent implements OnInit, OnDestroy {
  @Input() flight: FlightWithStreams;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() passengers$: Observable<FinnairPassengerItem[]>;
  @Input() seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;
  @Input() flightIsActive$: Observable<boolean>;
  @Input() includedSeats: FinnairServiceItem | null;
  @Input() availability$: Observable<EligibilitiesForFragments> = of({});
  @Input() canClearSeats = true;
  @Input() preSelectedPax$: Observable<string> = of(undefined);
  @Input() ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus>;
  @Input() notEligibleWhenCheckInClosed = false;

  @Output() changeSeats: EventEmitter<SeatChangeEvent> = new EventEmitter();
  @Output() subtotalChange: EventEmitter<Amount> = new EventEmitter();
  @Output() retrySeatmap: EventEmitter<FinnairItineraryItemFlight> = new EventEmitter();
  @Output() finishedReassigningSeats: EventEmitter<boolean> = new EventEmitter();

  subscriptions = new Subscription();

  constructor(private store$: Store<BookingAppState>) {}

  seatSelected(e: SeatChangeEvent): void {
    this.changeSeats.emit(e);
    this.updateSubTotal();
  }

  updateSubTotal(): void {
    this.subscriptions.add(
      this.store$.pipe(seatSelections(), take(1)).subscribe((allSeatSelections) => {
        const priceAndCurrencyCode = (
          allSeatSelections && allSeatSelections[this.flight.id] ? allSeatSelections[this.flight.id] : []
        )
          .filter((seatSelection) => !this.isIncludedSeatSelection(this.includedSeats, this.flight.id, seatSelection))
          .filter((seatSelection) => seatSelection.variant && seatSelection.pricePerItem && !seatSelection.paid)
          .reduce(
            (combined, currentSeatSelection) => {
              return {
                price: combined.price + Number(currentSeatSelection.pricePerItem.amount ?? 0),
                currencyCode: combined.currencyCode ?? currentSeatSelection.pricePerItem.currencyCode,
              };
            },
            { price: 0, currencyCode: undefined }
          );

        this.subtotalChange.emit({
          amount: String(priceAndCurrencyCode?.price),
          currencyCode: priceAndCurrencyCode?.currencyCode,
        });
      })
    );
  }

  ngOnInit(): void {
    this.updateSubTotal();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private isIncludedSeatSelection(
    includedSeats: FinnairServiceItem | null,
    flightId: string,
    seatSelection: TravelerService
  ): boolean {
    return servicesForFlightAndTravelerId(includedSeats, flightId, seatSelection.travelerId).some(
      ({ seatNumber }) => seatNumber === seatSelection.variant
    );
  }

  emitFinishedReassigningSeats(): void {
    this.finishedReassigningSeats.emit();
  }
}
